import { ErrorInfo } from "react";
import DarkBlueButton from "../components/atoms/buttons/DarkBlueButton";
import { MdErrorOutline } from "react-icons/md";
import { useUserData } from "../lib/contexts/UserContext";
import { useNavigate } from "react-router-dom";
import { sultanHospitals } from "../lib/utils/constants";
import { useHospDocData } from "../lib/contexts/HospitalDoctorContext";

interface ErrorFallbackScreenProps {
  error?: Error;
  resetErrorBoundary?: () => void;
}

function ErrorFallbackScreen({ error, resetErrorBoundary }: ErrorFallbackScreenProps) {
  const { userData, handleLogout, handleSultanLogout } = useUserData();
  const navigate = useNavigate();
  const { hospitalID } = useHospDocData();

  function handleReload() {
    if (resetErrorBoundary) {
      resetErrorBoundary();
    }
    window.location.reload();
  }

  async function handleLogoutOnError() {
    if (sultanHospitals.includes(hospitalID || "")) {
      await handleSultanLogout();
      navigate("/sultan/login");
      window.location.reload();
    } else {
      await handleLogout();
      navigate("/");
      window.location.reload();
    }
  }

  return (
    <div className="h-screen w-screen items-center flex justify-center flex-col gap-12">
      <MdErrorOutline className="text-[150px] text-blue-500" />
      <div className="flex flex-col items-center gap-4 max-w-[600px] px-4">
        <h1 className="text-2xl text-center">
          Oops! Something went wrong. Please reload the page. If the issue
          persists, try logging in again.
        </h1>
        {error && (
          <div className="w-full bg-red-50 border border-red-200 rounded-lg p-4 mt-4">
            <p className="text-red-800 font-medium mb-2">Error Details:</p>
            <p className="text-red-600 font-mono text-sm break-all">{error.message}</p>
            {error.stack && (
              <details className="mt-2">
                <summary className="text-red-800 text-sm cursor-pointer">Stack Trace</summary>
                <pre className="mt-2 text-xs text-red-600 overflow-x-auto">
                  {error.stack}
                </pre>
              </details>
            )}
          </div>
        )}
      </div>
      <div className="flex gap-4">
        <DarkBlueButton handleClick={handleReload} name="Reload" />
        <DarkBlueButton handleClick={handleLogoutOnError} name="Logout" />
      </div>
    </div>
  );
}

export default ErrorFallbackScreen;
